import React, { useState, useEffect } from 'react';
import { BlockMath, InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css'; // Import KaTeX CSS for styling
import { FiUpload, FiFileText, FiBook, FiGlobe, FiLayers, FiBookOpen, FiUser, FiYoutube, FiCpu, FiGrid, FiEdit3, FiHelpCircle, FiFile, FiShare2, FiCalendar, FiChevronDown, FiChevronUp, FiCamera, FiRefreshCw, FiZoomIn, FiZoomOut, FiAward, FiEdit2, FiPlus, FiTrash2, FiCopy } from 'react-icons/fi';
import MermaidRenderer from './MermaidRenderer';
import HandwrittenNotes from './HandwrittenNotes';
import { useUser } from './UserContext';
import { useNavigate } from 'react-router-dom';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import Confetti from 'react-confetti';
import { PieChart, Pie, Tooltip, Legend, Cell, ResponsiveContainer } from 'recharts'; // New import for charts
import bg from '../images/best.jpg'

// Simple Markdown parser for headings and lists
const parseMarkdown = (markdownText) => {
  if (!markdownText) return [];

  const lines = markdownText.split('\n');
  const elements = [];
  let currentList = null;

  lines.forEach((line, index) => {
    line = line.trim();

    // Handle LaTeX equations in different formats
    let remainingLine = line;
    while (remainingLine) {
      // Match $...$ (inline math)
      let latexMatch = remainingLine.match(/\$([^$]+)\$/);
      // Match \(...\) (inline math with parentheses)
      if (!latexMatch) latexMatch = remainingLine.match(/\\\(([^()]+)\\\)/);
      // Match \[...\] (display math)
      if (!latexMatch) latexMatch = remainingLine.match(/\\\[(.*?)\\\]/);

      if (latexMatch) {
        if (currentList) {
          elements.push({ type: 'list', items: currentList });
          currentList = null;
        }
        const latexText = latexMatch[1].trim();
        elements.push({
          type: 'latex',
          text: latexText,
          isDisplay: latexMatch[0].startsWith('\\['), // Flag for display math
        });
        remainingLine = remainingLine.replace(latexMatch[0], '').trim();
        if (remainingLine) {
          elements.push({ type: 'paragraph', text: remainingLine });
        }
      } else {
        // Handle headings (#, ##)
        if (line.startsWith('# ')) {
          if (currentList) {
            elements.push({ type: 'list', items: currentList });
            currentList = null;
          }
          elements.push({ type: 'heading', level: 1, text: line.slice(2).trim() });
        } else if (line.startsWith('## ')) {
          if (currentList) {
            elements.push({ type: 'list', items: currentList });
            currentList = null;
          }
          elements.push({ type: 'heading', level: 2, text: line.slice(3).trim() });
        }
        // Handle bulleted lists (-, *)
        else if (line.startsWith('- ') || line.startsWith('* ')) {
          if (!currentList) currentList = [];
          currentList.push(line.slice(2).trim());
        }
        // Handle plain text
        else if (line) {
          if (currentList) {
            elements.push({ type: 'list', items: currentList });
            currentList = null;
          }
          elements.push({ type: 'paragraph', text: line });
        }
        remainingLine = null; // Exit the while loop
      }
    }
  });

  // Push any remaining list
  if (currentList) {
    elements.push({ type: 'list', items: currentList });
  }

  return elements;
};
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A569BD', '#F1948A', '#58D68D', '#3498DB', '#E74C3C', '#2ECC71'];
// Render parsed Markdown as styled components

// ... (other imports and code remain the same)

const renderParsedMarkdown = (parsedElements, baseText) => {
  return parsedElements.map((element, index) => {
    switch (element.type) {
      case 'heading':
        return (
          <h1
            key={index}
            className={`font-bold ${element.level === 1 ? 'text-2xl sm:text-3xl' : 'text-xl sm:text-2xl'} ${baseText} mb-4`}
          >
            {element.text}
          </h1>
        );
      case 'list':
        return (
          <ul key={index} className={`list-disc pl-6 sm:pl-8 text-sm sm:text-base ${baseText} mb-4`}>
            {element.items.map((item, i) => (
              <li key={i} className="mb-2">{item}</li>
            ))}
          </ul>
        );
      case 'paragraph':
        return (
          <p key={index} className={`text-sm sm:text-base ${baseText} mb-4`}>
            {element.text}
          </p>
        );
      case 'latex':
        return (
          <div
            key={index}
            className={`text-sm sm:text-base ${baseText} ${element.isDisplay ? 'my-4' : 'inline-block'} mb-4`}
          >
            {element.isDisplay ? (
              <BlockMath
                math={element.text}
                errorColor="#ff0000"
                renderError={(error) => (
                  <span style={{ color: '#ff0000' }}>Error rendering equation: {error.message}</span>
                )}
              />
            ) : (
              <InlineMath
                math={element.text}
                errorColor="#ff0000"
                renderError={(error) => (
                  <span style={{ color: '#ff0000' }}>Error rendering equation: {error.message}</span>
                )}
              />
            )}
          </div>
        );
      default:
        return null;
    }
  });
};
// Medal Popup Component
const MedalPopup = ({ medal, onClose }) => {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <Confetti width={window.innerWidth} height={window.innerHeight} recycle={false} numberOfPieces={200} />
      <div className="bg-gray-800 bg-opacity-90 backdrop-blur-lg rounded-3xl p-6 sm:p-8 text-center max-w-md">
        <FiAward className="text-5xl sm:text-6xl mx-auto mb-4" style={{ color: medal === 'Gold' ? '#FFD700' : medal === 'Silver' ? '#C0C0C0' : '#CD7F32' }} />
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Congratulations!</h2>
        <p className="text-base sm:text-lg text-white mb-6">You've earned the {medal} Medal for answering {medal === 'Gold' ? 100 : medal === 'Silver' ? 50 : 20} questions!</p>
        <button
          className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-3 sm:p-4 text-white font-semibold text-base sm:text-lg hover:scale-105 transition-all duration-300"
          onClick={onClose}
        >
          Continue Studying
        </button>
      </div>
    </div>
  );
};
function getGuestId() {
  let id = localStorage.getItem("guest_id");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("guest_id", id);
  }
  return id;
}


const PDFProcessor = () => {
  const [mode, setMode] = useState('questions');
  const [text, setText] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [youtubeUrlInput, setYoutubeUrlInput] = useState('');
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [followUpQ, setFollowUpQ] = useState('');
  const [followUpA, setFollowUpA] = useState('');
  const [userAnswers, setUserAnswers] = useState({});
  const [score, setScore] = useState(null);
  const [difficulty, setDifficulty] = useState('medium');
  const [questionCount, setQuestionCount] = useState(5);
  const [slideCount, setSlideCount] = useState(3);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [flashcards, setFlashcards] = useState([]);
  const [currentFlashcardIndex, setCurrentFlashcardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [vocabularyList, setVocabularyList] = useState([]);
  const [humanizedText, setHumanizedText] = useState('');
  const [correctedText, setCorrectedText] = useState('');
  const [mindmapCode, setMindmapCode] = useState('');
  const [diagramCode, setDiagramCode] = useState('');
  const [diagramType, setDiagramType] = useState('flowchart');
  const [handwrittenText, setHandwrittenText] = useState('');
  const [snapshotTopic, setSnapshotTopic] = useState(''); // New 
  const [handwrittenStyle, setHandwrittenStyle] = useState('neat');
  const [fontFamily, setFontFamily] = useState('Arial');
  const [fontColor, setFontColor] = useState('#000000');
  const [slideBgColor, setSlideBgColor] = useState('#FFFFFF');
  const [pptContentSource, setPptContentSource] = useState('input');
  const [pptPrompt, setPptPrompt] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [paperFormat, setPaperFormat] = useState('pdf');
  const [studyPlan, setStudyPlan] = useState([]);
  const [studyPlanDuration, setStudyPlanDuration] = useState(7);
  const [studyPlanHoursPerDay, setStudyPlanHoursPerDay] = useState(2);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [referralFeature, setReferralFeature] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [subjectData, setSubjectData] = useState([]);
  const [answerFeedback, setAnswerFeedback] = useState({});
  const [strongSubjects, setStrongSubjects] = useState([]);
  const [weakSubjects, setWeakSubjects] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
 const [transcription, setTranscription] = useState('');
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [knowledgeGraphDocs, setKnowledgeGraphDocs] = useState([]);
  const [courseName, setCourseName] = useState('');
  const [doubts, setDoubts] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [summaryTone, setSummaryTone] = useState('medium');
  const [solveQuestionsTone, setSolveQuestionsTone] = useState('medium');
  const [timeTaken, setTimeTaken] = useState(null);


  const [featureCounts, setFeatureCounts] = useState({
    ppt: 0,
    humanize: 0,
    handwritten: 0,
    vocabulary: 0,
    summary: 0,
    questions: 0,
    diagram: 0,
    'solve-questions': 0,
    'study-plan': 0,
    'grammar-correction': 0
  });
  const [progress, setProgress] = useState({
    questions: { answered: 0, total: 0 },
    flashcards: { reviewed: 0, total: 0 },
    studyPlan: { completedTasks: 0, totalTasks: 0 },
    totalQuestionsAnswered: 0
  });
  const [medals, setMedals] = useState({
    bronze: false,
    silver: false,
    gold: false
  });
  const [showMedalPopup, setShowMedalPopup] = useState(null);
  const [isProgressExpanded, setIsProgressExpanded] = useState(false);

  const { user, getToken } = useUser();
  const navigate = useNavigate();
  
  const stopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      setIsRecording(false);
      setMediaRecorder(null);
    }
  };
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { 
        mimeType: 'audio/webm;codecs=opus' 
      });
      const chunks = [];
  
      recorder.ondataavailable = (e) => {
        chunks.push(e.data);
      };
  
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };
  
      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (err) {
      console.error('Error starting recording:', err);
      setResult({ error: 'Failed to access microphone. Please check permissions.' });
    }
  };
  
  const transcribeAudio = async () => {
    if (!audioBlob) {
      setResult({ error: 'No audio recorded to transcribe.' });
      return;
    }
  
    try {
      const formData = new FormData();
      
      // Convert to a supported format if needed
      const audioFile = new File([audioBlob], `recording-${Date.now()}.webm`, {
        type: 'audio/webm'
      });
      
      formData.append('file', audioFile);
  
      const headers = await getAuthHeaders();
      delete headers['Content-Type'];
  
      setProcessing(true);
      const response = await fetch('https://arush1234sharma-querio-backend.hf.space/api/upload-audio', {
        method: 'POST',
        headers,
        body: formData,
      });
  
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }
  
      const data = await response.json();
      if (data.extracted_text) {
        setTranscription(data.extracted_text);
        setText(prevText => prevText ? `${prevText}\n\n${data.extracted_text}` : data.extracted_text);
        setAudioBlob(null);
        setResult({ success: 'Audio transcribed successfully!' });
      } else {
        throw new Error('No transcription returned.');
      }
    } catch (err) {
      console.error('Error transcribing audio:', err);
      
      // Extract error message from JSON if possible
      let errorMsg = err.message;
      try {
        const errorObj = JSON.parse(err.message);
        if (errorObj.detail) {
          errorMsg = errorObj.detail;
        }
      } catch (e) {
        // Not JSON, use original message
      }
      
      setResult({ error: `Transcription error: ${errorMsg}` });
    } finally {
      setProcessing(false);
    }
  };
  const getAuthHeaders = async (contentType = 'application/json') => {
    try {
      const token = await getToken();
      const headers = { 'Content-Type': contentType };
  
      // **Add guest_id always**
      headers['x-guest-id'] = getGuestId();
  
      if (token) headers['Authorization'] = `Bearer ${token}`;
      return headers;
    } catch (err) {
      console.error('Failed to fetch token:', err);
      setResult({ error: 'Authentication error. Please sign in again.' });
      setShowSignInModal(true);
      throw err;
    }
  };
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (user) {
        try {
          const response = await fetch('https://arush1234sharma-querio-backend.hf.space/api/dashboard', {
            method: 'GET',
            headers: await getAuthHeaders(),
          });
          if (response.ok) {
            const data = await response.json();
            setSubjectData(data.subjects || []);
            setStrongSubjects(data.strong_subjects || []);
            setWeakSubjects(data.weak_subjects || []);
          }
        } catch (err) {
          console.error('Failed to fetch dashboard data:', err);
        }
      }
    };
    fetchDashboardData();
  }, [user]);

  // Load total questions answered and medals from localStorage or backend
  useEffect(() => {
    const savedProgress = localStorage.getItem('userProgress');
    if (savedProgress) {
      const parsed = JSON.parse(savedProgress);
      setProgress(prev => ({ ...prev, totalQuestionsAnswered: parsed.totalQuestionsAnswered || 0 }));
      setMedals(parsed.medals || { bronze: false, silver: false, gold: false });
    }

    if (user) {
      const fetchProgress = async () => {
        try {
          const response = await fetch('https://arush1234sharma-querio-backend.hf.space/api/user-progress', {
            method: 'GET',
            headers: await getAuthHeaders(),
          });
          if (response.ok) {
            const data = await response.json();
            setProgress(prev => ({ ...prev, totalQuestionsAnswoered: data.totalQuestionsAnswered || 0 }));
            setMedals(data.medals || { bronze: false, silver: false, gold: false });
          }
        } catch (err) {
          console.error('Failed to fetch user progress:', err);
        }
      };
      fetchProgress();
    }
  }, [user]);

  

  // Save progress to localStorage and backend
  const saveProgress = async (newProgress, newMedals) => {
    localStorage.setItem('userProgress', JSON.stringify({
      totalQuestionsAnswered: newProgress.totalQuestionsAnswered,
      medals: newMedals
    }));

    if (user) {
      try {
        await fetch('https://arush1234sharma-querio-backend.hf.space/api/update-user-progress', {
          method: 'POST',
          headers: await getAuthHeaders(),
          body: JSON.stringify({
            user_id: user.id,
            totalQuestionsAnswered: newProgress.totalQuestionsAnswered,
            medals: newMedals
          }),
        });
      } catch (err) {
        console.error('Failed to save user progress:', err);
      }
    }
  };

  // Update progress and check for medals
  useEffect(() => {
    let total = 0;
    let completed = 0;

    // Questions progress
    if (mode === 'questions' && result?.questions) {
      const totalQuestions = result.questions.length;
      const answeredQuestions = Object.keys(userAnswers).length;
      setProgress(prev => ({
        ...prev,
        questions: { answered: answeredQuestions, total: totalQuestions }
      }));
      total += totalQuestions;
      completed += answeredQuestions;
    }

    // Flashcards progress
    if (mode === 'flashcards' && flashcards.length > 0) {
      const totalFlashcards = flashcards.length;
      const reviewedFlashcards = currentFlashcardIndex + 1;
      setProgress(prev => ({
        ...prev,
        flashcards: { reviewed: reviewedFlashcards, total: totalFlashcards }
      }));
      total += totalFlashcards;
      completed += reviewedFlashcards;
    }

    // Study plan progress
    if (mode === 'study-plan' && studyPlan.length > 0) {
      const totalTasks = studyPlan.reduce((sum, day) => sum + day.tasks.length, 0);
      const completedTasks = studyPlan.reduce((sum, day) => sum + day.tasks.length, 0);
      setProgress(prev => ({
        ...prev,
        studyPlan: { completedTasks, totalTasks }
      }));
      total += totalTasks;
      completed += completedTasks;
    }

    // Calculate overall progress
    const overallProgress = total > 0 ? Math.round((completed / total) * 100) : 0;
    setProgress(prev => ({ ...prev, overall: overallProgress }));
  }, [userAnswers, flashcards, currentFlashcardIndex, studyPlan, mode, result]);

  // Check for medal achievements
  useEffect(() => {
    const totalAnswered = progress.totalQuestionsAnswered;
    const newMedals = { ...medals };

    if (totalAnswered >= 100 && !medals.gold) {
      newMedals.gold = true;
      setShowMedalPopup('Gold');
    } else if (totalAnswered >= 50 && !medals.silver) {
      newMedals.silver = true;
      setShowMedalPopup('Silver');
    } else if (totalAnswered >= 20 && !medals.bronze) {
      newMedals.bronze = true;
      setShowMedalPopup('Bronze');
    }

    if (JSON.stringify(newMedals) !== JSON.stringify(medals)) {
      setMedals(newMedals);
      saveProgress(progress, newMedals);
    }
  }, [progress.totalQuestionsAnswered]);

  // Modified to preserve text state
  const clearOutputStates = () => {
    setUrlInput('');
    setYoutubeUrlInput('');
    setFileToUpload(null);
    setResult(null);
    setScore(null);
    setUserAnswers({});
    setFollowUpA('');
    setFollowUpQ('');
    setFlashcards([]);
    setCurrentFlashcardIndex(0);
    setIsFlipped(false);
    setVocabularyList([]);
    setHumanizedText('');
    setCorrectedText('');
    setMindmapCode('');
    setDiagramCode('');
    setHandwrittenText('');
    setPptPrompt('');
    setStudyPlan([]);
    setProgress(prev => ({
      ...prev,
      questions: { answered: 0, total: 0 },
      flashcards: { reviewed: 0, total: 0 },
      studyPlan: { completedTasks: 0, totalTasks: 0 },
      overall: 0
    }));
  };

  const handleTextChange = (e) => {
    clearOutputStates();
    setText(e.target.value);
  };

  const handleUrlChange = (e) => {
    clearOutputStates();
    setUrlInput(e.target.value);
  };

  const handleFileChange = (e) => {
    clearOutputStates();
    const file = e.target.files[0];
    setFileToUpload(file || null);
  };
  
  
  const handleCaptureImage = (e) => {
    clearOutputStates();
    const file = e.target.files[0];
    if (file) {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const newFile = new File([file], `captured-image-${timestamp}.jpg`, { type: file.type });
      setFileToUpload(newFile);
    } else {
      setFileToUpload(null);
      setResult({ error: 'No image captured. Please try again or ensure camera access is granted.' });
    }
  };

  const handleQuestionCountChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setQuestionCount('');
    } else {
      const num = parseInt(value, 10);
      if (!isNaN(num)) {
        setQuestionCount(Math.max(1, Math.min(10, num)));
      }
    }
  };

  const handleQuestionCountBlur = () => {
    if (questionCount === '' || isNaN(questionCount)) {
      setQuestionCount(5);
    }
  };

  const handleSlideCountChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setSlideCount('');
    } else {
      const num = parseInt(value, 10);
      if (!isNaN(num)) {
        setSlideCount(Math.max(1, Math.min(10, num)));
      }
    }
  };

  const handleSlideCountBlur = () => {
    if (slideCount === '' || isNaN(slideCount)) {
      setSlideCount(3);
    }
  };

  const handleStudyPlanDurationChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setStudyPlanDuration('');
    } else {
      const num = parseInt(value, 10);
      if (!isNaN(num)) {
        setStudyPlanDuration(Math.max(1, Math.min(30, num)));
      }
    }
  };

  const handleStudyPlanHoursChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setStudyPlanHoursPerDay('');
    } else {
      const num = parseInt(value, 10);
      if (!isNaN(num)) {
        setStudyPlanHoursPerDay(Math.max(1, Math.min(8, num)));
      }
    }
  };

  const handleSubmitAnswers = () => {
    let correct = 0;
    const feedback = {};
    const endTime = Date.now();
    const duration = Math.floor((endTime - startTime) / 1000); // seconds
    setTimeTaken(duration);
  
    if (result && result.questions) {
      result.questions.forEach((q, idx) => {
        const userAnswer = userAnswers[idx];   // what the user selected
        const isCorrect = userAnswer === q.answer;  // backend uses `answer` (full text)
  
        if (isCorrect) correct++;
  
        feedback[idx] = {
          isCorrect,
          correctAnswer: q.answer,  // show the actual correct text
          explanation: q.explanation || 'No explanation provided.'
        };
      });
  
      setScore(correct);
      setAnswerFeedback(feedback);
  
      // Update local progress
      setProgress(prev => {
        const newTotalAnswered = prev.totalQuestionsAnswered + Object.keys(userAnswers).length;
        const updatedProgress = {
          ...prev,
          questions: { ...prev.questions, answered: Object.keys(userAnswers).length },
          totalQuestionsAnswered: newTotalAnswered
        };
        saveProgress(updatedProgress, medals);
        return updatedProgress;
      });
  
      // Send answers to backend
      if (user) {
        const subject = result.subject || 'Other'; // fallback
        const answers = result.questions.map((q, idx) => ({
          question_idx: idx,
          user_answer: userAnswers[idx] || '',
          correct_answer: q.answer   // send text-based answer
        }));
  
        fetch('https://arush1234sharma-querio-backend.hf.space/api/submit-answers', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            user_id: user.id,
            subject,
            answers
          })
        })
          .then(response => response.json())
          .then(data => console.log('Answers submitted:', data))
          .catch(err => console.error('Failed to submit answers:', err));
      }
    }
  };
  

  const checkFeatureLimit = async (feature, slideCountForPPT = 1) => {
    if (feature === 'ppt') {
      // Bypass limit check for PPT feature
      return true;
    }
  
    const limits = {
      ppt: slideCountForPPT > 1 ? 0 : 1,
      humanize: 10,
      handwritten: 10,
      vocabulary: 10,
      summary: 10,
      questions: 10,
      diagram: 10,
      'solve-questions': 10,
      'study-plan': 10,
      'grammar-correction': 10
    };
  
    if (featureCounts[feature] >= limits[feature]) {
      if (!user) {
        setReferralFeature(feature);
        setShowSignInModal(true);
        return false;
      }
  
      try {
        const response = await fetch('https://arush1234sharma-querio-backend.hf.space/api/generate-referral', {
          method: 'POST',
          headers: await getAuthHeaders(),
          body: JSON.stringify({ feature, user_id: user.id }),
        });
        if (!response.ok) throw new Error(await response.text());
        const data = await response.json();
        setReferralFeature(feature);
        setReferralLink(data.referral_link);
        setShowReferralModal(true);
        return false;
      } catch (err) {
        setResult({ error: `Failed to generate referral link: ${err.message}` });
        return false;
      }
    }
    return true;
  };

  const checkReferralStatus = async (feature) => {
    if (!user) return;
    try {
      console.log(`Checking referral status for feature: ${feature}`);
      const response = await fetch('https://arush1234sharma-querio-backend.hf.space/api/check-referral-status', {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ user_id: user.id, feature }),
      });
      if (!response.ok) throw new Error(await response.text());
      const data = await response.json();
      console.log(`Referral status for ${feature}:`, data);
      if (data.success && data.referrals_count >= 3) {
        setFeatureCounts(prev => ({ ...prev, [feature]: 0 }));
        setShowReferralModal(false);
        setResult({ success: `Feature ${feature} unlocked! You can now use it again.` });
      } else {
        setResult({
          message: `Waiting for ${3 - (data.referrals_count || 0)} more referrals to unlock ${feature}.`,
        });
      }
    } catch (err) {
      console.error(`Failed to check referral status for ${feature}:`, err);
      setResult({ error: 'Failed to check referral status. Please try again later.' });
    }
  };

  useEffect(() => {
    if (showReferralModal && user && referralFeature) {
      const interval = setInterval(() => {
        checkReferralStatus(referralFeature);
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [showReferralModal, user, referralFeature]);
  
  // const handleGenerateQuestions = async () => {
   
  //   setStartTime(Date.now());   // start timer
  //   setTimeTaken(null);         // reset old time
  // };
  

  
  const handleGenerate = async (retries = 3) => {
    setProcessing(true);
    setResult(null);
    clearOutputStates();

    let contentToProcess = text;
    const API_BASE = 'https://arush1234sharma-querio-backend.hf.space';

    if (mode === 'ppt' && slideCount > 1) {
      if (!(await checkFeatureLimit('ppt', slideCount))) {
        setProcessing(false);
        return;
      }

      

    } else if (['humanize', 'handwritten', 'vocabulary', 'summary', 'questions', 'diagram', 'solve-questions', 'study-plan', 'grammar-correction'].includes(mode)) {
      if (!(await checkFeatureLimit(mode))) {
        setProcessing(false);
        return;
      }
    }

    const getContent = async (attempt = 0) => {
      let fetchedContent = '';
      if (youtubeUrlInput.trim()) {
        try {
          const response = await fetch(`${API_BASE}/api/fetch-youtube-content`, {
            method: 'POST',
            headers: await getAuthHeaders(),
            body: JSON.stringify({ youtube_url: youtubeUrlInput }),
          });
          if (!response.ok) throw new Error(await response.text() || `HTTP ${response.status}`);
          const data = await response.json();
          fetchedContent = data.extracted_text || '';
          setYoutubeUrlInput('');
          if (!fetchedContent.trim()) throw new Error("No readable content found from the YouTube URL.");
        } catch (err) {
          if (attempt < retries - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
            return getContent(attempt + 1);
          }
          setResult({ error: `YouTube processing error: ${err.message}` });
          setProcessing(false);
          return;
        }
      } else if (urlInput.trim()) {
        try {
          const response = await fetch(`${API_BASE}/api/fetch-and-extract-url`, {
            method: 'POST',
            headers: await getAuthHeaders(),
            body: JSON.stringify({ url: urlInput }),
          });
          if (!response.ok) throw new Error(await response.text() || `HTTP ${response.status}`);
          const data = await response.json();
          fetchedContent = data.extracted_text || '';
          setUrlInput('');
          if (!fetchedContent.trim()) throw new Error("No readable text found from the URL.");
        } catch (err) {
          if (attempt < retries - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
            return getContent(attempt + 1);
          }
          setResult({ error: `URL processing error: ${err.message}` });
          setProcessing(false);
          return;
        }
      } else if (fileToUpload) {
        try {
          // âœ… Validate before sending
          if (!fileToUpload) {
            setResult({ error: "No file selected." });
            setProcessing(false);
            return;
          }
      
          const formData = new FormData();
          formData.append("file", fileToUpload);
      
          // âœ… Donâ€™t force multipart headers, let browser handle it
          const fileHeaders = await getAuthHeaders();
          delete fileHeaders["Content-Type"]; // FormData sets this automatically
      
          const response = await fetch(`${API_BASE}/api/upload-and-extract`, {
            method: "POST",
            headers: fileHeaders,
            body: formData,
          });
      
          if (!response.ok) throw new Error(await response.text() || `HTTP ${response.status}`);
      
          const data = await response.json();
          fetchedContent = data.extracted_text || "";
      
          if (!fetchedContent.trim()) {
            throw new Error("No readable text found in the uploaded file.");
          }
      
          // âœ… Only clear after success
          setFileToUpload(null);
        } catch (err) {
          if (attempt < retries - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
            return getContent(attempt + 1);
          }
          setResult({ error: `File processing error: ${err.message}` });
          setProcessing(false);
          return;
        }
      
      
      } else if (mode === 'ppt' && pptContentSource === 'prompt' && pptPrompt.trim()) {
        try {
          const response = await fetch(`${API_BASE}/api/humanize-text`, {
            method: 'POST',
            headers: await getAuthHeaders(),
            body: JSON.stringify({ text: `Generate detailed slide content for a presentation based on this prompt: ${pptPrompt}` }),
          });
          if (!response.ok) throw new Error(await response.text() || `HTTP ${response.status}`);
          const data = await response.json();
          fetchedContent = data.humanized_text || '';
          setPptPrompt('');
          if (!fetchedContent.trim()) throw new Error("No content generated from the prompt.");
        } catch (err) {
          if (attempt < retries - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
            return getContent(attempt + 1);
          }
          setResult({ error: `Prompt processing error: ${err.message}` });
          setProcessing(false);
          return;
        }
      }

      if (fetchedContent.trim()) {
        setText(prevText => prevText ? `${prevText}\n\n${fetchedContent}` : fetchedContent);
        return fetchedContent;
      }
      return contentToProcess.trim() || null;
    };

    const fetchedContent = await getContent();
    contentToProcess = fetchedContent || text;

    if (!contentToProcess) {
      setResult({ error: 'No content provided to process.' });
      setProcessing(false);
      return;
    }

    try {
      let endpoint = '';
      let payload = {};

      switch (mode) {
        case 'questions':
          endpoint = `${API_BASE}/api/generate-questions`;
          payload = { text: contentToProcess, difficulty, count: parseInt(questionCount, 10) || 5 };
          break;
        case 'summary':
            endpoint = `${API_BASE}/api/summarize`;
            payload = { text: contentToProcess, tone: summaryTone };
            break;
        case 'solve-questions':
            endpoint = `${API_BASE}/api/solve-questions`;
            payload = { text: contentToProcess, tone: solveQuestionsTone };
            break;

            
        case 'question-paper':
           endpoint = `${API_BASE}/api/generate-question-paper`;
           payload = { 
             text: contentToProcess, 
             subject: courseName || "General Studies",
             difficulty,
             sections: ["MCQ", "Short Answer", "Long Answer"],
             format: paperFormat
           };
           break;
            
        case 'flashcards':
          endpoint = `${API_BASE}/api/generate-flashcards`;
          payload = { text: contentToProcess };
          break;
        case 'vocabulary':
          endpoint = `${API_BASE}/api/generate-vocabulary`;
          payload = { text: contentToProcess };
          break;
        case 'humanize':
          endpoint = `${API_BASE}/api/humanize-text`;
          payload = { text: contentToProcess };
          break;
        case 'mindmap':
          endpoint = `${API_BASE}/api/generate-mindmap`;
          payload = { text: contentToProcess };
          break;
          
          
        case 'doubts':
          endpoint = `${API_BASE}/api/generate-doubts`;
          payload = { text: contentToProcess };
          break;
            // ... rest of cases ...
         
        case 'diagram':
          endpoint = `${API_BASE}/api/generate-diagram`;
          payload = { text: contentToProcess, diagram_type: diagramType };
          break;
        case 'handwritten':
          endpoint = `${API_BASE}/api/generate-handwritten`;
          payload = { text: contentToProcess, style: handwrittenStyle };
          break;
        
        case 'ppt':
          endpoint = `${API_BASE}/api/generate-ppt`;
          payload = { 
            text: contentToProcess, 
            slide_count: parseInt(slideCount, 10) || 3, 
            font_family: fontFamily, 
            font_color: fontColor, 
            slide_bg_color: slideBgColor,
            font_size: 18
          };
          break;
          case 'snapshot': {
            const endpoint = `${API_BASE}/api/generate-snapshot`;
            const payload = { topic: snapshotTopic || text };
            
            const response = await fetch(endpoint, {
              method: 'POST',
              headers: await getAuthHeaders(),
              body: JSON.stringify(payload),
            });
            
            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(errorText || `HTTP ${response.status}`);
            }
            
            // Handle as Blob for PDF download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${snapshotTopic || 'Snapshot'}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            
            setResult({ success: "Snapshot PDF downloaded successfully." });
            setProcessing(false);
            return; // Stop further execution
          }
          
          
        case 'study-plan':
          endpoint = `${API_BASE}/api/generate-study-plan`;
          payload = { 
            text: contentToProcess, 
            duration_days: parseInt(studyPlanDuration, 10) || 7,
            hours_per_day: parseInt(studyPlanHoursPerDay, 10) || 2
          };
          break;
        case 'grammar-correction':
          endpoint = `${API_BASE}/api/correct-grammar`;
          payload = { text: contentToProcess };
          break;
          case 'knowledge-graph':
         endpoint = `${API_BASE}/api/generate-knowledge-graph`;
          payload = {
         course_name: courseName,
         documents: knowledgeGraphDocs,
    };
         break;
        default:
          setResult({ error: 'Invalid mode selected.' });
          setProcessing(false);
          return;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (mode === 'question-paper') {
        if (!response.ok) {
          const errorText = await response.text();
          setResult({ error: errorText || `HTTP ${response.status}` });
          setProcessing(false);
          return;
        }
      
        const blob = await response.blob();
        const fileURL = URL.createObjectURL(blob);
      
        setResult({ file_url: fileURL });
        setProcessing(false);
        return;  // 👈 skip JSON parsing
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      if (mode === 'ppt') {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `presentation-${new Date().toISOString().split('T')[0]}.pptx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        setResult({ success: `Presentation downloaded successfully with ${slideCount} slides.` });
        setFeatureCounts(prev => ({ ...prev, ppt: prev.ppt + 1 }));
      } else {
        const data = await response.json();
        switch (mode) {
          case 'questions':
          case 'summary':
            setResult(data);
            setFeatureCounts(prev => ({ ...prev, [mode]: prev[mode] + 1 }));
            break;
          case 'flashcards':
            setFlashcards(data.flashcards || []);
            setCurrentFlashcardIndex(0);
            setIsFlipped(false);
            setProgress(prev => ({
              ...prev,
              flashcards: { reviewed: 0, total: data.flashcards?.length || 0 }
            }));
            break;
          case 'vocabulary':
            setVocabularyList(data.vocabulary || []);
            setFeatureCounts(prev => ({ ...prev, vocabulary: prev.vocabulary + 1 }));
            break;
          case 'humanize':
            setHumanizedText(data.humanized_text || '');
            setFeatureCounts(prev => ({ ...prev, humanize: prev.humanize + 1 }));
            break;
          case 'doubts':
            setDoubts(data.doubts || []);
            setFeatureCounts(prev => ({ ...prev, doubts: prev.doubts + 1 }));
            break;
          case 'grammar-correction':
            setCorrectedText(data.corrected_text || '');
            setFeatureCounts(prev => ({ ...prev, 'grammar-correction': prev['grammar-correction'] + 1 }));
            break;
          case 'mindmap':
            setMindmapCode(data.mermaid_code || '');
            setFeatureCounts(prev => ({ ...prev, mindmap: prev.mindmap + 1 }));
            break;
          case 'diagram':
            setDiagramCode(data.mermaid_code || '');
            setFeatureCounts(prev => ({ ...prev, diagram: prev.diagram + 1 }));
            break;
          case 'handwritten':
            setHandwrittenText(data.handwritten_text || '');
            setFeatureCounts(prev => ({ ...prev, handwritten: prev.handwritten + 1 }));
            break;
          
          case 'solve-questions':
            setResult(data);
            setFeatureCounts(prev => ({ ...prev, 'solve-questions': prev['solve-questions'] + 1 }));
            break;
            case 'knowledge-graph':
              setResult(data); // Keep mermaid_code, graph, conflicts, gaps, etc.
              setFeatureCounts(prev => ({
                  ...prev,
                  'knowledge-graph': (prev['knowledge-graph'] || 0) + 1
              }));
              break;
          

          case 'study-plan':
            setStudyPlan(data.study_plan || []);
            setFeatureCounts(prev => ({ ...prev, 'study-plan': prev['study-plan'] + 1 }));
            setProgress(prev => ({
              ...prev,
              studyPlan: {
                completedTasks: 0,
                totalTasks: data.study_plan?.reduce((sum, day) => sum + day.tasks.length, 0) || 0
              }
            }));
            break;
        }
      }
    } catch (err) {
      setResult({ error: err.message });
    } finally {
      setProcessing(false);
    }
  };
  
  const handleFollowUp = async () => {
    if (!followUpQ || !result?.summary) return;
    setFollowUpA('');

    try {
      const response = await fetch('https://arush1234sharma-querio-backend.hf.space/api/follow-up-question', {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ summary: result.summary, question: followUpQ }),
      });
      if (!response.ok) throw new Error(await response.text() || `HTTP ${response.status}`);
      const data = await response.json();
      setFollowUpA(data.answer || '');
    } catch (err) {
      setFollowUpA(`Error: ${err.message}`);
    }
  };

  const handleShareReferral = () => {
    navigator.clipboard.writeText(referralLink);
    alert('Referral link copied to clipboard! Share it with 3 people to unlock the feature.');
  };

  const handleCloseReferralModal = () => {
    setShowReferralModal(false);
    setReferralFeature('');
    setReferralLink('');
  };

  const handleCloseSignInModal = () => {
    setShowSignInModal(false);
    setReferralFeature('');
  };

  const handleSignInRedirect = () => {
    navigate('/signin');
  };

  const goToNextFlashcard = () => {
    setIsFlipped(false);
    setCurrentFlashcardIndex((prev) => {
      const newIndex = (prev + 1) % flashcards.length;
      setProgress(prev => ({
        ...prev,
        flashcards: { ...prev.flashcards, reviewed: Math.min(newIndex + 1, prev.flashcards.total) }
      }));
      return newIndex;
    });
  };

  const goToPreviousFlashcard = () => {
    setIsFlipped(false);
    setCurrentFlashcardIndex((prev) => {
      const newIndex = (prev - 1 + flashcards.length) % flashcards.length;
      setProgress(prev => ({
        ...prev,
        flashcards: { ...prev.flashcards, reviewed: Math.min(newIndex + 1, prev.flashcards.total) }
      }));
      return newIndex;
    });
  };

  const handleClear = () => {
    setText('');
    clearOutputStates();
  };
  const handleExampleClick = (exampleText) => {
    clearOutputStates();
    setText(exampleText);
  };
  const baseText = 'text-white';
  const inputBg = 'bg-gray-900 border-gray-700';
  const inputBorder = 'border-gray-700';
  const inputPlaceholder = 'placeholder-gray-400';
  const glassEffectPrimary = 'glass-effect';

  const getSlidePreview = (text, count) => {
    const slides = text.split('\n\n').slice(0, count);
    while (slides.length < count) slides.push('');
    return slides;
  };

  const toggleProgressExpand = () => {
    setIsProgressExpanded(!isProgressExpanded);
  };

  const calculateProgressPercentage = (completed, total) => {
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const getNextMedalTarget = () => {
    const totalAnswered = progress.totalQuestionsAnswered;
    if (totalAnswered < 20) return { count: 20 - totalAnswered, medal: 'Bronze' };
    if (totalAnswered < 50) return { count: 50 - totalAnswered, medal: 'Silver' };
    if (totalAnswered < 100) return { count: 100 - totalAnswered, medal: 'Gold' };
    return { count: 0, medal: 'None' };
  };
  const handleCopyOutput = async () => {
    let outputText = '';
    switch (mode) {
      case 'questions':
        if (result?.questions) {
          outputText = result.questions.map((q, idx) => (
            `Question ${idx + 1}: ${q.question}\nOptions:\n${q.options.join('\n')}\nAnswer: ${q.answer}\nExplanation: ${q.explanation || 'No explanation provided.'}`
          )).join('\n\n');
        }
        break;
      case 'flashcards':
        if (flashcards.length > 0) {
          outputText = flashcards.map((fc, idx) => (
            `Flashcard ${idx + 1}:\nFront: ${fc.front}\nBack: ${fc.back}`
          )).join('\n\n');
        }
        break;
      case 'vocabulary':
        if (vocabularyList.length > 0) {
          outputText = vocabularyList.map((item, idx) => (
            `Word ${idx + 1}: ${item.word}\nDefinition: ${item.definition}`
          )).join('\n\n');
        }
        break;
      case 'humanize':
        if (humanizedText) {
          outputText = humanizedText;
        }
        break;

      case 'grammar-correction':
        if (correctedText) {
          outputText = correctedText;
        }
        break;
      case 'mindmap':
        if (mindmapCode) {
          outputText = mindmapCode;
        }
        break;
      case 'diagram':
        if (diagramCode) {
          outputText = diagramCode;
        }
        break;
      case 'handwritten':
        if (handwrittenText) {
          outputText = handwrittenText;
        }
        break;
      case 'solve-questions':
        if (result?.answers) {
          outputText = result.answers.map((item, idx) => (
            `Question ${idx + 1}: ${item.question}\nAnswer: ${item.answer}`
          )).join('\n\n');
        }
        break;
      case 'study-plan':
        if (studyPlan.length > 0) {
          outputText = studyPlan.map((day, idx) => (
            `Day ${day.day} (${day.duration}):\n${day.tasks.map((task, taskIdx) => (
              `Task ${taskIdx + 1}: ${task.task} - ${task.description}`
            )).join('\n')}`
          )).join('\n\n');
        }
        break;
      case 'doubts':
        if (doubts.length > 0) {
          outputText = doubts.map((doubt, idx) => (
            `Doubt ${idx + 1}: ${doubt.question}\nContext: ${doubt.context}${doubt.hint ? `\nHint: ${doubt.hint}` : ''}`
          )).join('\n\n');
        }
        break;
      case 'knowledge-graph':
        if (result?.mermaid_code) {
          outputText = result.mermaid_code;
          if (result.conflicts?.length > 0) {
            outputText += '\n\nConflicts:\n' + result.conflicts.map((conflict, idx) => (
              `Conflict ${idx + 1}: ${conflict.concept}\n${conflict.definitions.map((def, i) => (
                `${conflict.sources[i]}: ${def}`
              )).join('\n')}`
            )).join('\n\n');
          }
          if (result.gaps?.length > 0) {
            outputText += '\n\nGaps:\n' + result.gaps.map((gap, idx) => (
              `Gap ${idx + 1}: ${gap.concept} (Appears in: ${gap.sources.join(', ')})`
            )).join('\n\n');
          }
        }
        break;
      case 'question-paper':
        if (result?.file_url) {
          outputText = `Question Paper URL: ${result.file_url}`;
        }
        break;
      default:
        outputText = 'No content available to copy.';
    }

    if (outputText) {
      try {
        await navigator.clipboard.writeText(outputText);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const exampleTexts = [
    {
      title: 'Math Example',
      text: 'Solve the quadratic equation: $x^2 - 5x + 6 = 0$. Find the roots using the quadratic formula.',
    },
    {
      title: 'Book Example',
      text: 'Summarize The Alchemist Novel.',
    },
    {
      title: 'Science Example',
      text: 'Photosynthesis is the process by which green plants convert carbon dioxide and water into glucose and oxygen using sunlight. The equation is: $6CO_2 + 6H_2O \\rightarrow C_6H_{12}O_6 + 6O_2$.',
    },
  ];
  const buttonGradient = 'button-gradient';
  return (
    <div className="font-poppins relative z-10 bg-black">
      <div 
           className="lumin-landing-bg" 
           style={{
             backgroundImage: `url(${bg})`,
             backgroundSize: "cover",
             backgroundPosition: "center",
           }}
      ></div>
      <div className="max-w-7xl mx-auto py-6 sm:py-8 px-4 sm:px-6 relative z-20">
        <div className="flex justify-between items-center mb-10 sm:mb-16">
         
        </div>
        <div className="text-center mb-16 sm:mb-20 fade-in-up">
          <h2 className="text-4xl sm:text-6xl font-semibold mb-4 sm:mb-6 leading-tight text-shadow-lg text-white">
            Your one stop AI-powered study solution
          </h2>
          <p className="text-base sm:text-xl text-gray-300 max-w-4xl mx-auto opacity-90 fade-in-up-delay-2">
            NexNotes AI instantly helps you revise your study material without actually wasting time going through whole docs.
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 fade-in-up-delay-3">
          <div className="space-y-6 sm:space-y-8 fade-in-up-delay-4">
            <div className={`${glassEffectPrimary} rounded-3xl p-6 sm:p-8 fade-in-up-delay-4`}>
              <div className="mb-4 sm:mb-6 fade-in-up-delay-4">
                <div className={`flex items-center ${inputBg} ${inputBorder} rounded-3xl p-3 sm:p-4 ${baseText} ${inputPlaceholder} focus-within:ring-2 focus-within:ring-blue-500 transition-all duration-300`}>
                  <FiGlobe className="text-xl sm:text-2xl mr-3 sm:mr-4 text-gray-400" />
                  <input
                    type="text"
                    className="flex-grow bg-transparent outline-none text-base sm:text-lg"
                    placeholder="Web page URL"
                    value={urlInput}
                    onChange={handleUrlChange}
                  />
                </div>
              </div>
              <div className="flex items-center my-4 sm:my-6">
                <div className={`flex-grow border-t ${inputBorder} opacity-50`}></div>
                <span className="flex-shrink mx-3 sm:mx-4 text-gray-400 text-base sm:text-lg font-semibold">OR</span>
                <div className={`flex-grow border-t ${inputBorder} opacity-50`}></div>
              </div>
              <textarea
                className={`w-full h-64 sm:h-80 ${inputBg} ${inputBorder} rounded-3xl p-4 sm:p-6 ${baseText} ${inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 resize-y text-base sm:text-lg`}
                placeholder="Paste your notes or text here"
                value={text}
                onChange={handleTextChange}
                
              />
              {text === '' && (
                  <div className="space-y-3">
                    <p className={`text-base sm:text-lg ${baseText} font-semibold`}>Try an Example:</p>
                    <div className="flex flex-wrap gap-2 sm:gap-3">
                      {exampleTexts.map((example, index) => (
                        <button
                          key={index}
                          className={`${glassEffectPrimary} rounded-3xl p-2 sm:p-3 ${baseText} text-sm sm:text-base hover:bg-gradient-to-r hover:from-blue-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105`}
                          onClick={() => handleExampleClick(example.text)}
                        >
                          {example.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 mt-6 sm:mt-8 flex-wrap">
                <label className="flex-1 min-w-[160px] sm:min-w-[200px]">
                  <input
                    type="file"
                    accept=".pdf,.docx,.ppt,.pptx,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <div className={`${glassEffectPrimary} rounded-3xl p-4 sm:p-5 text-center cursor-pointer hover:border-blue-500 transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 ${baseText} text-base sm:text-lg font-medium transform hover:scale-102`}>
                    <FiUpload className="text-xl sm:text-2xl" />
                    <span>Upload File ({fileToUpload ? fileToUpload.name : 'PDF/Word/PPT/Image'})</span>
                  </div>
                </label>
                <label className="flex-1 min-w-[160px] sm:min-w-[200px]">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleCaptureImage}
                    className="hidden"
                  />

                  <div className={`${glassEffectPrimary} rounded-3xl p-4 sm:p-5 text-center cursor-pointer hover:border-blue-500 transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 ${baseText} text-base sm:text-lg font-medium transform hover:scale-102`}>
                    <FiCamera className="text-xl sm:text-2xl" />
                    <span>Capture Image ({fileToUpload ? fileToUpload.name : 'Take a Photo'})</span>
                  </div>
                </label>
              </div>
              <div className={`${glassEffectPrimary} rounded-3xl p-2 flex flex-wrap gap-2 mt-4 sm:mt-6`}>
                {['questions', 'summary', 'flashcards', 'vocabulary', 'humanize', 'mindmap', 'diagram','question-paper', 'handwritten', 'solve-questions', 'ppt', 'study-plan', 'grammar-correction','knowledge-graph', 'doubts', 'snapshot'].map(m => (
                  <button
                    key={m}
                    className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-3 rounded-2xl transition-all duration-300 flex-grow font-semibold text-sm sm:text-base transform hover:scale-105  ${mode === m ? buttonGradient : ""}`}
                    onClick={() => setMode(m)}
                  >
                    {m === 'questions' && <FiFileText className="text-base sm:text-lg" />}
                    {m === 'summary' && <FiBook className="text-base sm:text-lg" />}
                    {m === 'flashcards' && <FiLayers className="text-base sm:text-lg" />}
                    {m === 'vocabulary' && <FiBookOpen className="text-base sm:text-lg" />}
                    {m === 'humanize' && <FiUser className="text-base sm:text-lg" />}
                    {m === 'mindmap' && <FiGrid className="text-base sm:text-lg" />}
                    {m === 'diagram' && <FiCpu className="text-base sm:text-lg" />}
                    {m === 'handwritten' && <FiEdit3 className="text-base sm:text-lg" />}
                    {m === 'solve-questions' && <FiHelpCircle className="text-base sm:text-lg" />}
                    {m === 'ppt' && <FiFile className="text-base sm:text-lg" />}
                    {m === 'study-plan' && <FiCalendar className="text-base sm:text-lg" />}
                    {m === 'grammar-correction' && <FiEdit2 className="text-base sm:text-lg" />}
                    {m === 'snapshot' && <FiCpu className="text-base sm:text-lg" />}
                    {m === 'knowledge-graph' && <FiCpu className="text-base sm:text-lg" />}
                    {m === 'question-paper' && <FiEdit2 className="text-base sm:text-lg" />}
                    {m === 'doubts' && <FiHelpCircle className="text-base sm:text-lg" />}
                    {m.charAt(0).toUpperCase() + m.slice(1).replace('-', ' ')}
                  </button>
                ))}
              </div>
              {mode === 'questions' && (
                <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-6">
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    <div className="flex-1">
                      <label className={`block text-sm sm:text-base font-medium ${baseText} mb-2`}>Difficulty</label>
                      <select
                        className={`w-full ${inputBg} ${inputBorder} rounded-3xl p-3 sm:p-4 ${baseText} ${inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-lg`}
                        value={difficulty}
                        onChange={(e) => setDifficulty(e.target.value)}
                      >
                        <option value="easy">Easy</option>
                        <option value="medium">Medium</option>
                        <option value="hard">Hard</option>
                      </select>
                    </div>
                    <div className="flex-1">
                      <label className={`block text-sm sm:text-base font-medium ${baseText} mb-2`}>Number of Questions</label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        className={`w-full ${inputBg} ${inputBorder} rounded-3xl p-3 sm:p-4 ${baseText} ${inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-lg`}
                        value={questionCount}
                        onChange={handleQuestionCountChange}
                        onBlur={handleQuestionCountBlur}
                        placeholder="Enter number (1-10)"
                      />
                    </div>
                  </div>
                </div>
              )}
             

{mode === 'question-paper' && (
  <div className="mt-4 space-y-4">


    <div>
      <label className="block mb-1">Select Format:</label>
      <select
        value={paperFormat}
        onChange={(e) => setPaperFormat(e.target.value)}
        className="border px-2 py-1 rounded w-full text-black"
      >
        <option value="pdf">PDF</option>
        <option value="docx">Word (DOCX)</option>
      </select>
    </div>
  </div>
)}

                              {mode === 'knowledge-graph' && (
                    <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-6">
                        <div>
                            <label className={`block text-sm sm:text-base font-medium ${baseText} mb-2`}>Course/Subject Name</label>
                              <input
                                type="text"
                                className={`w-full ${inputBg} ${inputBorder} rounded-3xl p-3 sm:p-4 ${baseText} ${inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-lg`}
                                value={courseName}
                                onChange={(e) => setCourseName(e.target.value)}
                                placeholder="e.g., Biology 101, Calculus II"
                            />
                        </div>
                        <div>
                            <label className={`block text-sm sm:text-base font-medium ${baseText} mb-2`}>Documents to Analyze</label>
                            <div className="space-y-3">
                                {knowledgeGraphDocs.map((doc, idx) => (
                                    <div key={idx} className="flex items-center gap-3">
                                        <textarea
                                            className={`flex-1 ${inputBg} ${inputBorder} rounded-3xl p-3 sm:p-4 ${baseText} ${inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-lg`}
                                            value={doc}
                                            onChange={(e) => {
                                                const newDocs = [...knowledgeGraphDocs];
                                                newDocs[idx] = e.target.value;
                                                setKnowledgeGraphDocs(newDocs);
                                            }}
                                            placeholder="Paste document content"
                                        />
                                        <button
                                            onClick={() => {
                                                const newDocs = [...knowledgeGraphDocs];
                                                newDocs.splice(idx, 1);
                                                setKnowledgeGraphDocs(newDocs);
                                            }}
                                            className={`${glassEffectPrimary} rounded-full p-2 sm:p-3 ${baseText}`}
                                        >
                                            <FiTrash2 className="text-xl sm:text-2xl" />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    onClick={() => setKnowledgeGraphDocs([...knowledgeGraphDocs, ''])}
                                    className={`${glassEffectPrimary} rounded-3xl p-3 sm:p-4 ${baseText} text-base sm:text-lg font-semibold hover:bg-gradient-to-r hover:from-blue-600 hover:to-purple-600 transition-all duration-300`}
                                >
                                    <FiPlus className="inline mr-2" />
                                    Add Another Document
                                </button>
                            </div>
                        </div>
                    </div>
                )}
              {mode === 'ppt' && (
                <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-6">
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    <div className="flex-1">
                      <label className={`block text-sm sm:text-base font-medium ${baseText} mb-2`}>Number of Slides</label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        className={`w-full ${inputBg} ${inputBorder} rounded-3xl p-3 sm:p-4 ${baseText} ${inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-lg`}
                        value={slideCount}
                        onChange={handleSlideCountChange}
                        onBlur={handleSlideCountBlur}
                        placeholder="Enter number (1-10)"
                      />
                    </div>
                    <div className="flex-1">
                      <label className={`block text-sm sm:text-base font-medium ${baseText} mb-2`}>Font Family</label>
                      <select
                        className={`w-full ${inputBg} ${inputBorder} rounded-3xl p-3 sm:p-4 ${baseText} ${inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-lg`}
                        value={fontFamily}
                        onChange={(e) => setFontFamily(e.target.value)}
                      >
                        <option value="Arial">Arial</option>
                        <option value="Times New Roman">Times New Roman</option>
                        <option value="Calibri">Calibri</option>
                        <option value="Verdana">Verdana</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    <div className="flex-1">
                      <label className={`block text-sm sm:text-base font-medium ${baseText} mb-2`}>Font Color</label>
                      <input
                        type="color"
                        className={`w-full h-12 sm:h-14 ${inputBg} ${inputBorder} rounded-3xl p-2 sm:p-3 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        value={fontColor}
                        onChange={(e) => setFontColor(e.target.value)}
                      />
                    </div>
                    <div className="flex-1">
                      <label className={`block text-sm sm:text-base font-medium ${baseText} mb-2`}>Slide Background Color</label>
                      <input
                        type="color"
                        className={`w-full h-12 sm:h-14 ${inputBg} ${inputBorder} rounded-3xl p-2 sm:p-3 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        value={slideBgColor}
                        onChange={(e) => setSlideBgColor(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}
              {mode === 'snapshot' && (
  <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-6">
    <div>
      <label className={`block text-sm sm:text-base font-medium ${baseText} mb-2`}>Snapshot Topic</label>
      <input
        type="text"
        className={`w-full ${inputBg} ${inputBorder} rounded-3xl p-3 sm:p-4 ${baseText} ${inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-lg`}
        value={snapshotTopic}
        onChange={(e) => setSnapshotTopic(e.target.value)}
        placeholder="Enter topic for snapshot"
      />
    </div>
  </div>
)}
              {mode === 'study-plan' && (
                <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-6">
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    <div className="flex-1">
                      <label className={`block text-sm sm:text-base font-medium ${baseText} mb-2`}>Duration (Days)</label>
                      <input
                        type="number"
                        min="1"
                        max="30"
                        className={`w-full ${inputBg} ${inputBorder} rounded-3xl p-3 sm:p-4 ${baseText} ${inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-lg`}
                        value={studyPlanDuration}
                        onChange={handleStudyPlanDurationChange}
                        placeholder="Enter number (1-30)"
                      />
                    </div>
                    <div className="flex-1">
                      <label className={`block text-sm sm:text-base font-medium ${baseText} mb-2`}>Hours Per Day</label>
                      <input
                        type="number"
                        min="1"
                        max="8"
                        className={`w-full ${inputBg} ${inputBorder} rounded-3xl p-3 sm:p-4 ${baseText} ${inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-lg`}
                        value={studyPlanHoursPerDay}
                        onChange={handleStudyPlanHoursChange}
                        placeholder="Enter number (1-8)"
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 mt-6 sm:mt-8">
                <button
                  className={`flex-1 ${glassEffectPrimary} rounded-3xl p-4 sm:p-5 text-center ${baseText} text-base sm:text-lg font-semibold hover:bg-gradient-to-r hover:from-blue-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 sm:gap-3`}
                  onClick={handleGenerate}
                  disabled={processing || (!text && !urlInput && !youtubeUrlInput && !fileToUpload && !(mode === 'ppt' && pptContentSource === 'prompt' && pptPrompt))}
                >
                  <FiCpu className="text-xl sm:text-2xl" />
                  {processing ? 'Processing...' : 'Generate'}
                </button>
                <button
                  className={`flex-1 ${glassEffectPrimary} rounded-3xl p-4 sm:p-5 text-center ${baseText} text-base sm:text-lg font-semibold hover:bg-gradient-to-r hover:from-red-600 hover:to-red-800 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2 sm:gap-3`}
                  onClick={handleClear}
                >
                  <FiRefreshCw className="text-xl sm:text-2xl" />
                  Clear
                </button>
              </div>
            </div>
          </div>
          <div className="space-y-6 sm:space-y-8">
            <div className={`${glassEffectPrimary} rounded-3xl p-6 sm:p-8`}>
              <h2 className={`text-xl sm:text-2xl font-bold ${baseText} mb-4 sm:mb-6`}>Output</h2>
              {processing ? (
                <div className="text-center text-gray-400 text-base sm:text-lg">
                  Processing your request...
                </div>
              ) : result?.error ? (
                <div className="text-red-400 text-base sm:text-lg">{result.error}</div>
              ) : result?.success ? (
                <div className="text-green-400 text-base sm:text-lg">{result.success}</div>
              ) : result?.message ? (
                <div className="text-yellow-400 text-base sm:text-lg">{result.message}</div>
              ) : (
                <>
              <button
                    className={`${glassEffectPrimary} rounded-3xl p-2 mb-6 sm:p-3 ${baseText} text-sm sm:text-base hover:bg-gradient-to-r hover:from-blue-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 flex items-center gap-2`}
                    onClick={handleCopyOutput}
                  >
                    <FiCopy className="text-xl sm:text-2xl" />
                    {isCopied ? 'Copied!' : 'Copy Output'}
               </button>
                  {mode === 'questions' && result?.questions?.length > 0 && (
                    <div className="space-y-6 sm:space-y-8">
                      {result.questions.map((q, idx) => (
                        <div key={idx} className="space-y-3 sm:space-y-4">
                          <p className={`text-base sm:text-lg ${baseText} font-semibold`}>
                            {idx + 1}. {q.text}
                          </p>
                          <div className="space-y-2 sm:space-y-3">
                            {q.options && q.options.length > 0 ? (
                              q.options.map((option, optIdx) => (
                                <label key={optIdx} className="flex items-center gap-2 sm:gap-3">
                                  <input
                                    type="radio"
                                    name={`question-${idx}`}
                                    value={option}
                                    checked={userAnswers[idx] === option}
                                    onChange={(e) => setUserAnswers({ ...userAnswers, [idx]: e.target.value })}
                                    className="text-blue-500 focus:ring-blue-500 h-4 w-4 sm:h-5 sm:w-5"
                                  />
                                  <span className={`text-sm sm:text-base ${baseText}`}>{option}</span>
                                </label>
                              ))
                            ) : (
                              <input
                                type="text"
                                className={`w-full ${inputBg} ${inputBorder} rounded-3xl p-3 sm:p-4 ${baseText} ${inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-lg`}
                                value={userAnswers[idx] || ''}
                                onChange={(e) => setUserAnswers({ ...userAnswers, [idx]: e.target.value })}
                                placeholder="Type your answer here"
                              />
                            )}
                          </div>
                        </div>
                      ))}
                      <button
                        className={`w-full ${glassEffectPrimary} rounded-3xl p-4 sm:p-5 text-center ${baseText} text-base sm:text-lg font-semibold hover:bg-gradient-to-r hover:from-blue-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105`}
                        onClick={handleSubmitAnswers}
                        disabled={Object.keys(userAnswers).length === 0}
                      >
                        Submit Answers
                      </button>
                      {score !== null && (
                        <div className="text-base sm:text-lg text-green-400">
                          Score: {score}/{result.questions.length}
                        </div>
                      )}
                    </div>
                  )}
                  {mode === 'summary' && result?.summary && (
                    <div className="space-y-4 sm:space-y-6">
                      {renderParsedMarkdown(parseMarkdown(result.summary), baseText)}
                      <div className="mt-4 sm:mt-6">
                        <h3 className={`text-lg sm:text-xl font-semibold ${baseText} mb-2 sm:mb-3`}>Ask a Follow-Up Question</h3>
                        <input
                          type="text"
                          className={`w-full ${inputBg} ${inputBorder} rounded-3xl p-3 sm:p-4 ${baseText} ${inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-lg`}
                          value={followUpQ}
                          onChange={(e) => setFollowUpQ(e.target.value)}
                          placeholder="Type your follow-up question here"
                        />
                        <button
                          className={`w-full mt-3 sm:mt-4 ${glassEffectPrimary} rounded-3xl p-4 sm:p-5 text-center ${baseText} text-base sm:text-lg font-semibold hover:bg-gradient-to-r hover:from-blue-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105`}
                          onClick={handleFollowUp}
                          disabled={!followUpQ}
                        >
                          Submit Question
                        </button>
                        {followUpA && (
                          <div className="mt-4 sm:mt-6">
                            <h4 className={`text-base sm:text-lg font-semibold ${baseText} mb-2`}>Answer:</h4>
                            {renderParsedMarkdown(parseMarkdown(followUpA), baseText)}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {mode === 'summary' && (
  <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-6">
    <div>
      <label className={`block text-sm sm:text-base font-medium ${baseText} mb-2`}>Tone</label>
      <select
        className={`w-full ${inputBg} ${inputBorder} rounded-3xl p-3 sm:p-4 ${baseText} ${inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-lg`}
        value={summaryTone}
        onChange={(e) => setSummaryTone(e.target.value)}
      >
        <option value="soft">Soft & Friendly</option>
        <option value="medium">Medium & Balanced</option>
        <option value="strict">Strict & Formal</option>
      </select>
    </div>
  </div>
)}

{mode === 'solve-questions' && (
  <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-6">
    <div>
      <label className={`block text-sm sm:text-base font-medium ${baseText} mb-2`}>Tone</label>
      <select
        className={`w-full ${inputBg} ${inputBorder} rounded-3xl p-3 sm:p-4 ${baseText} ${inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-lg`}
        value={solveQuestionsTone}
        onChange={(e) => setSolveQuestionsTone(e.target.value)}
      >
        <option value="soft">Soft & Friendly</option>
        <option value="medium">Medium & Balanced</option>
        <option value="strict">Strict & Formal</option>
      </select>
    </div>
  </div>
)}
                  {mode === 'flashcards' && flashcards.length > 0 && (
                    <div className="space-y-4 sm:space-y-6">
                      <div className="relative perspective-1000">
                        <div
                          className={`relative w-full h-64 sm:h-80 rounded-3xl transition-transform duration-500 transform-style-preserve-3d ${isFlipped ? 'rotate-y-180' : ''}`}
                          onClick={() => setIsFlipped(!isFlipped)}
                        >
                          <div className="absolute inset-0 bg-gray-800 bg-opacity-90 backdrop-blur-lg rounded-3xl flex items-center justify-center p-4 sm:p-6 backface-hidden">
                            <p className={`text-base sm:text-lg ${baseText} text-center font-semibold`}>{flashcards[currentFlashcardIndex].front}</p>
                          </div>
                          <div className="absolute inset-0 bg-gray-800 bg-opacity-90 backdrop-blur-lg rounded-3xl flex items-center justify-center p-4 sm:p-6 backface-hidden rotate-y-180">
                            <p className={`text-base sm:text-lg ${baseText} text-center`}>{flashcards[currentFlashcardIndex].back}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <button
                          className={`flex items-center gap-2 sm:gap-3 ${glassEffectPrimary} rounded-3xl p-3 sm:p-4 ${baseText} text-base sm:text-lg font-semibold hover:bg-gradient-to-r hover:from-blue-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105`}
                          onClick={goToPreviousFlashcard}
                          disabled={currentFlashcardIndex === 0}
                        >
                          <FiChevronUp className="text-xl sm:text-2xl" />
                          Previous
                        </button>
                        <span className={`text-base sm:text-lg ${baseText}`}>
                          {currentFlashcardIndex + 1}/{flashcards.length}
                        </span>
                        <button
                          className={`flex items-center gap-2 sm:gap-3 ${glassEffectPrimary} rounded-3xl p-3 sm:p-4 ${baseText} text-base sm:text-lg font-semibold hover:bg-gradient-to-r hover:from-blue-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105`}
                          onClick={goToNextFlashcard}
                          disabled={currentFlashcardIndex === flashcards.length - 1}
                        >
                          Next
                          <FiChevronDown className="text-xl sm:text-2xl" />
                        </button>
                      </div>
                    </div>
                  )}
                  {mode === 'vocabulary' && vocabularyList.length > 0 && (
                    <div className="space-y-4 sm:space-y-6">
                      {vocabularyList.map((item, idx) => (
                        <div key={idx} className="border-b border-gray-700 pb-3 sm:pb-4">
                          <p className={`text-base sm:text-lg ${baseText} font-semibold`}>{item.word}</p>
                          <p className={`text-sm sm:text-base ${baseText}`}>{item.definition}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {mode === 'doubts' && doubts.length > 0 && (
  <div className="space-y-4 sm:space-y-6">
    <h3 className={`text-lg sm:text-xl font-semibold ${baseText} mb-4`}>Potential Doubts While Studying</h3>
    {doubts.map((doubt, idx) => (
      <div key={idx} className={`${glassEffectPrimary} rounded-3xl p-4 sm:p-6 border-l-4 border-yellow-500`}>
        <div className="flex items-start gap-3 sm:gap-4">
          <FiHelpCircle className="text-yellow-500 text-xl sm:text-2xl mt-1 flex-shrink-0" />
          <div className="flex-grow">
            <p className={`text-base sm:text-lg ${baseText} font-semibold mb-2 sm:mb-3`}>
              {doubt.question}
            </p>
            <p className={`text-sm sm:text-base ${baseText} opacity-90`}>
              {doubt.context}
            </p>
            {doubt.hint && (
              <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-gray-700 bg-opacity-50 rounded-2xl">
                <p className={`text-xs sm:text-sm ${baseText} font-medium mb-1`}> Hint:</p>
                <p className={`text-sm sm:text-base ${baseText} opacity-80`}>{doubt.hint}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    ))}
  </div>
)}
                  {mode === 'humanize' && humanizedText && (
                    <div className="space-y-4 sm:space-y-6">
                      {renderParsedMarkdown(parseMarkdown(humanizedText), baseText)}
                    </div>
                  )}
                  {mode === 'grammar-correction' && correctedText && (
                    <div className="space-y-4 sm:space-y-6">
                      {renderParsedMarkdown(parseMarkdown(correctedText), baseText)}
                    </div>
                  )}
                  {mode === 'mindmap' && mindmapCode && (
                    <TransformWrapper>
                      {({ zoomIn, zoomOut }) => (
                        <>
                          <div className="flex justify-end mb-3 sm:mb-4">
                            <button onClick={zoomIn} className={`${glassEffectPrimary} rounded-full p-2 sm:p-3 mr-2 sm:mr-3 ${baseText}`}>
                              <FiZoomIn className="text-xl sm:text-2xl" />
                            </button>
                            <button onClick={zoomOut} className={`${glassEffectPrimary} rounded-full p-2 sm:p-3 ${baseText}`}>
                              <FiZoomOut className="text-xl sm:text-2xl" />
                            </button>
                          </div>
                          <TransformComponent>
                            <div className="w-full">
                              <MermaidRenderer code={mindmapCode} />
                            </div>
                          </TransformComponent>
                        </>
                      )}
                    </TransformWrapper>
                  )}
                  {mode === 'question-paper' && result && (
  <div className="mt-4">
    <a
      href={result.file_url || "#"}
      className="bg-blue-600 text-white px-4 py-2 rounded"
      download={`QuestionPaper.${paperFormat}`}
    >
      Download Question Paper
    </a>
  </div>
)}

                  {mode === 'diagram' && diagramCode && (
                    <TransformWrapper>
                      {({ zoomIn, zoomOut }) => (
                        <>
                          <div className="flex justify-end mb-3 sm:mb-4">
                            <select
                              className={`mr-2 sm:mr-3 ${inputBg} ${inputBorder} rounded-3xl p-2 sm:p-3 ${baseText} text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500`}
                              value={diagramType}
                              onChange={(e) => setDiagramType(e.target.value)}
                            >
                              <option value="flowchart">Flowchart</option>
                              <option value="sequence">Sequence</option>
                              <option value="class">Class</option>
                              <option value="state">State</option>
                              <option value="entity">Entity-Relationship</option>
                            </select>
                            <button onClick={zoomIn} className={`${glassEffectPrimary} rounded-full p-2 sm:p-3 mr-2 sm:mr-3 ${baseText}`}>
                              <FiZoomIn className="text-xl sm:text-2xl" />
                            </button>
                            <button onClick={zoomOut} className={`${glassEffectPrimary} rounded-full p-2 sm:p-3 ${baseText}`}>
                              <FiZoomOut className="text-xl sm:text-2xl" />
                            </button>
                          </div>
                          <TransformComponent>
                            <div className="w-full">
                              <MermaidRenderer code={diagramCode} />
                            </div>
                          </TransformComponent>
                        </>
                      )}
                    </TransformWrapper>
                  )}
                  {mode === 'knowledge-graph' && result?.mermaid_code && (
                          <div className="space-y-6">
                              <TransformWrapper>
                                  {({ zoomIn, zoomOut }) => (
                                      <>
                                          <div className="flex justify-end mb-3 sm:mb-4">
                                              <button onClick={zoomIn} className={`${glassEffectPrimary} rounded-full p-2 sm:p-3 mr-2 sm:mr-3 ${baseText}`}>
                                                  <FiZoomIn className="text-xl sm:text-2xl" />
                                              </button>
                                              <button onClick={zoomOut} className={`${glassEffectPrimary} rounded-full p-2 sm:p-3 ${baseText}`}>
                                                  <FiZoomOut className="text-xl sm:text-2xl" />
                                              </button>
                                          </div>
                                          <TransformComponent>
                                              <div className="w-full">
                                                  <MermaidRenderer code={result.mermaid_code} type="knowledge-graph" />
                                              </div>
                                          </TransformComponent>
                                      </>
                                  )}
                              </TransformWrapper>
                              
                              {result.conflicts?.length > 0 && (
                                  <div className={`${glassEffectPrimary} rounded-3xl p-4 sm:p-6`}>
                                      <h3 className={`text-lg sm:text-xl font-semibold ${baseText} mb-3 sm:mb-4`}>Potential Conflicts</h3>
                                      <div className="space-y-3">
                                          {result.conflicts.map((conflict, idx) => (
                                              <div key={idx} className="border-l-4 border-yellow-500 pl-4 py-2">
                                                  <p className={`text-base sm:text-lg font-medium ${baseText}`}>
                                                      {conflict.concept} has {conflict.definitions.length} different definitions
                                                  </p>
                                                  <ul className={`list-disc pl-6 sm:pl-8 text-sm sm:text-base ${baseText}`}>
                                                      {conflict.definitions.map((def, i) => (
                                                          <li key={i}>
                                                              <span className="font-medium">{conflict.sources[i]}:</span> {def}
                                                          </li>
                                                      ))}
                                                  </ul>
                                              </div>
                                          ))}
                                      </div>
                                  </div>
                              )}
        
                            {result.gaps?.length > 0 && (
                                <div className={`${glassEffectPrimary} rounded-3xl p-4 sm:p-6`}>
                                    <h3 className={`text-lg sm:text-xl font-semibold ${baseText} mb-3 sm:mb-4`}>Potential Knowledge Gaps</h3>
                                    <div className="space-y-3">
                                        {result.gaps.map((gap, idx) => (
                                            <div key={idx} className="border-l-4 border-red-500 pl-4 py-2">
                                                <p className={`text-base sm:text-lg font-medium ${baseText}`}>
                                                    {gap.concept} is mentioned but not defined in your materials
                                                </p>
                                                <p className={`text-sm sm:text-base ${baseText}`}>
                                                    Appears in: {gap.sources.join(', ')}
                                                </p>
                                            </div>
                                             ))}
                                       </div>
                                </div>
                            )}
                        </div>
                      )}
                  {mode === 'handwritten' && handwrittenText && (
                    <div className="space-y-4 sm:space-y-6">
                      <select
                        className={`w-full ${inputBg} ${inputBorder} rounded-3xl p-3 sm:p-4 ${baseText} ${inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-blue-500 text-base sm:text-lg mb-4 sm:mb-6`}
                        value={handwrittenStyle}
                        onChange={(e) => setHandwrittenStyle(e.target.value)}
                      >
                        <option value="neat">Neat</option>
                        <option value="casual">Casual</option>
                        <option value="messy">Messy</option>
                      </select>
                      <HandwrittenNotes text={handwrittenText} style={handwrittenStyle} />
                    </div>
                  )}
                  {mode === 'solve-questions' && result?.answers?.length > 0 && (
                    <div className="space-y-4 sm:space-y-6">
                      {result.answers.map((item, idx) => (
                        <div key={idx} className="border-b border-gray-700 pb-3 sm:pb-4">
                          <p className={`text-base sm:text-lg ${baseText} font-semibold`}>{item.question}</p>
                          <p className={`text-sm sm:text-base ${baseText}`}>{item.answer}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {mode === 'study-plan' && studyPlan.length > 0 && (
                    <div className="space-y-4 sm:space-y-6">
                      {studyPlan.map((day, idx) => (
                        <div key={idx} className="border-b border-gray-700 pb-3 sm:pb-4">
                          <h3 className={`text-lg sm:text-xl font-semibold ${baseText} mb-2 sm:mb-3`}>Day {day.day} ({day.duration})</h3>
                          <ul className={`list-disc pl-6 sm:pl-8 text-sm sm:text-base ${baseText}`}>
                            {day.tasks.map((task, taskIdx) => (
                              <li key={taskIdx} className="mb-2 sm:mb-3">
                                <span className="font-semibold">{task.task}:</span> {task.description}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            
                 {/* Progress & Dashboard Section */}
            <div className={`${glassEffectPrimary} rounded-3xl p-6 sm:p-8`}>
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h2 className={`text-xl sm:text-2xl font-bold ${baseText}`}>Progress & Dashboard</h2>
              <button
                className={`${glassEffectPrimary} rounded-full p-2 sm:p-3 ${baseText}`}
                onClick={toggleProgressExpand}
              >
                {isProgressExpanded ? <FiChevronUp className="text-xl sm:text-2xl" /> : <FiChevronDown className="text-xl sm:text-2xl" />}
              </button>
            </div>
            {isProgressExpanded && (
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <p className={`text-base sm:text-lg ${baseText} font-semibold`}>Questions</p>
                  <div className="w-full bg-gray-700 rounded-full h-2.5 sm:h-3 mt-2 sm:mt-3">
                    <div
                      className="bg-gradient-to-r from-blue-600 to-purple-600 h-2.5 sm:h-3 rounded-full"
                      style={{ width: `${calculateProgressPercentage(progress.questions.answered, progress.questions.total)}%` }}
                    ></div>
                  </div>
                  <p className={`text-sm sm:text-base ${baseText} mt-1 sm:mt-2`}>
                    {progress.questions.answered}/{progress.questions.total} answered
                  </p>
                </div>
                <div>
                  <p className={`text-base sm:text-lg ${baseText} font-semibold`}>Flashcards</p>
                  <div className="w-full bg-gray-700 rounded-full h-2.5 sm:h-3 mt-2 sm:mt-3">
                    <div
                      className="bg-gradient-to-r from-blue-600 to-purple-600 h-2.5 sm:h-3 rounded-full"
                      style={{ width: `${calculateProgressPercentage(progress.flashcards.reviewed, progress.flashcards.total)}%` }}
                    ></div>
                  </div>
                  <p className={`text-sm sm:text-base ${baseText} mt-1 sm:mt-2`}>
                    {progress.flashcards.reviewed}/{progress.flashcards.total} reviewed
                  </p>
                </div>
                <div>
                  <p className={`text-base sm:text-lg ${baseText} font-semibold`}>Study Plan</p>
                  <div className="w-full bg-gray-700 rounded-full h-2.5 sm:h-3 mt-2 sm:mt-3">
                    <div
                      className="bg-gradient-to-r from-blue-600 to-purple-600 h-2.5 sm:h-3 rounded-full"
                      style={{ width: `${calculateProgressPercentage(progress.studyPlan.completedTasks, progress.studyPlan.totalTasks)}%` }}
                    ></div>
                  </div>
                  <p className={`text-sm sm:text-base ${baseText} mt-1 sm:mt-2`}>
                    {progress.studyPlan.completedTasks}/{progress.studyPlan.totalTasks} tasks completed
                  </p>
                </div>
                <div>
                  <p className={`text-base sm:text-lg ${baseText} font-semibold`}>Medals</p>
                  <div className="flex gap-3 sm:gap-4 mt-2 sm:mt-3">
                    <div className={`flex items-center gap-2 sm:gap-3 ${medals.bronze ? 'text-yellow-500' : 'text-gray-500'}`}>
                      <FiAward className="text-xl sm:text-2xl" />
                      <span className="text-sm sm:text-base">Bronze</span>
                    </div>
                    <div className={`flex items-center gap-2 sm:gap-3 ${medals.silver ? 'text-gray-300' : 'text-gray-500'}`}>
                      <FiAward className="text-xl sm:text-2xl" />
                      <span className="text-sm sm:text-base">Silver</span>
                    </div>
                    <div className={`flex items-center gap-2 sm:gap-3 ${medals.gold ? 'text-yellow-300' : 'text-gray-500'}`}>
                      <FiAward className="text-xl sm:text-2xl" />
                      <span className="text-sm sm:text-base">Gold</span>
                    </div>
                  </div>
                  <p className={`text-sm sm:text-base ${baseText} mt-2 sm:mt-3`}>
                    Next medal: {getNextMedalTarget().count} more questions to earn {getNextMedalTarget().medal}
                  </p>
                </div>
                <div className="mt-6 sm:mt-8">
                   <p className={`text-base sm:text-lg ${baseText} font-semibold mb-4`}>Subject Performance</p>
                   {strongSubjects.length > 0 || weakSubjects.length > 0 ? (
                     <div className="space-y-4">
                       <div>
                         <p className={`text-sm sm:text-base ${baseText} font-semibold`}>Strong Subjects</p>
                         {strongSubjects.length > 0 ? (
                           <ul className={`list-disc pl-6 sm:pl-8 text-sm sm:text-base ${baseText}`}>
                             {strongSubjects.map((subject, index) => (
                               <li key={index} className="mb-2">
                                 {subject.subject}: {subject.correct} correct, {subject.incorrect} incorrect ({subject.accuracy}% accuracy)
                               </li>
                             ))}
                           </ul>
                         ) : (
                           <p className={`text-sm sm:text-base ${baseText}`}>No strong subjects yet. Answer more questions!</p>
                         )}
                       </div>
                       <div>
                         <p className={`text-sm sm:text-base ${baseText} font-semibold`}>Weak Subjects</p>
                         {weakSubjects.length > 0 ? (
                           <ul className={`list-disc pl-6 sm:pl-8 text-sm sm:text-base ${baseText}`}>
                             {weakSubjects.map((subject, index) => (
                               <li key={index} className="mb-2">
                                 {subject.subject}: {subject.correct} correct, {subject.incorrect} incorrect ({subject.accuracy}% accuracy)
                               </li>
                             ))}
                           </ul>
                         ) : (
                           <p className={`text-sm sm:text-base ${baseText}`}>No weak subjects yet. Answer more questions!</p>
                         )}
                       </div>
                     </div>
                   ) : (
                     <p className={`text-sm sm:text-base ${baseText}`}>No subject performance data yet. Answer some questions to see your strengths and weaknesses!</p>
                   )}
                 </div>           
                 {timeTaken !== null && (
                    <div className="text-sm font-semibold text-gray-300 mb-3">
                       â±ï¸ Time Taken: {Math.floor(timeTaken / 60)}m {timeTaken % 60}s
                     </div>
                    )}

                {/* Studied Subjects Dashboard */}
                <div className="mt-6 sm:mt-8">
                  <p className={`text-base sm:text-lg ${baseText} font-semibold mb-4`}>Studied Subjects Dashboard</p>
                  {subjectData.length > 0 ? (
                    <>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={subjectData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            fill="#8884d8"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {subjectData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                      <ul className={`list-disc pl-6 sm:pl-8 text-sm sm:text-base ${baseText} mt-4`}>
                        {subjectData.map((s, index) => (
                          <li key={index} className="mb-2">
                            {s.name}: {s.value} sessions
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <p className={`text-sm sm:text-base ${baseText}`}>No subjects studied yet. Process some content to see your dashboard!</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {showReferralModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-gray-800 bg-opacity-90 backdrop-blur-lg rounded-3xl p-6 sm:p-8 text-center max-w-md">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">Feature Limit Reached</h2>
            <p className="text-base sm:text-lg text-white mb-4 sm:mb-6">
              You've reached the limit for {referralFeature.replace('-', ' ')}. Invite 3 friends to unlock more uses!
            </p>
            <p className="text-sm sm:text-base text-gray-300 mb-4 sm:mb-6 break-all">{referralLink}</p>
            <button
              className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-3 sm:p-4 text-white font-semibold text-base sm:text-lg hover:scale-105 transition-all duration-300"
              onClick={handleShareReferral}
            >
              <FiShare2 className="inline mr-2 sm:mr-3" />
              Copy Referral Link
            </button>
            <button
              className="mt-3 sm:mt-4 text-gray-400 text-base sm:text-lg hover:text-white"
              onClick={handleCloseReferralModal}
            >
              Close
            </button>
          </div>
        </div>
      )}
      {showSignInModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-gray-800 bg-opacity-90 backdrop-blur-lg rounded-3xl p-6 sm:p-8 text-center max-w-md">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">Sign In Required</h2>
            <p className="text-base sm:text-lg text-white mb-4 sm:mb-6">
              You've reached the limit for {referralFeature.replace('-', ' ')}. Sign in to generate a referral link and unlock more uses!
            </p>
            <button
              className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-3 sm:p-4 text-white font-semibold text-base sm:text-lg hover:scale-105 transition-all duration-300"
              onClick={handleSignInRedirect}
            >
              Sign In
            </button>
            <button
              className="mt-3 sm:mt-4 text-gray-400 text-base sm:text-lg hover:text-white"
              onClick={handleCloseSignInModal}
            >
              Close
            </button>
          </div>
        </div>
      )}
      {showMedalPopup && (
        <MedalPopup
          medal={showMedalPopup}
          onClose={() => setShowMedalPopup(null)}
        />
      )}
    </div>
  </div>
  );
};

export default PDFProcessor;